/* External */
import {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
  memo,
} from 'react';
import { useParams, useRouteMatch } from 'react-router';
import { useLocation } from 'react-router-dom';

/* Ott-common */
import { PROVIDER } from '@dtvgo/backend-service/normalize';

/* Components */
import ScrollView from '../../../components/ScrollView';
import CarouselList from '../../../components/CarouselList';
import CatalogVodLander from './CatalogVodLander';

/* Providers */
import { useGtmLocalContext } from '../../../providers/GtmLocalProvider';

/* Utils */
import { getRem } from '../../../utils/functions';
import { ImageRatio, SPORT_PAGE_SET } from '../../../utils/constants';
import { getMatchingCollection, getWebAction } from '../../../utils/metaDataUtils';
import { useCountry } from '../../../providers/ConfigProvider';
import {
  formatPlaySource,
  GTM_CATEGORIES,
  GTM_EVENT_NAMES,
  GTM_SUBCATEGORIES,
} from '../../../utils/googleTags';

/* Hooks */
import useImageCard from '../../../hooks/useImageCard';
import useQuery from '../../../hooks/useQuery';
import useFetchCarousel from '../../../hooks/useFetchCarousel';

/* Styles */
import './Catalog.scss';

const IMG_WIDTH = 1920;
const IMG_DIMENSION = ImageRatio.SIXTEEN_BY_NINE;
const CARDS_PER_ROW = 6;
const PADDING_TOP_DESCRIPTION_REM = getRem(40);
const NUMBER_LINES_DESCRIPTION = 5;
const LINE_HEIGHT_DESCRIPTION = 1.5;
const FONT_SIZE_DESCRIPTION_REM = 1.2;
const HEIGHT_DESCRIPTION_REM = NUMBER_LINES_DESCRIPTION
    * LINE_HEIGHT_DESCRIPTION
    * FONT_SIZE_DESCRIPTION_REM
    + PADDING_TOP_DESCRIPTION_REM;

const MARGIN_BOTTOM_DESCRIPTION_REM = getRem(20);

function Catalog() {
  const { state: locationState } = useLocation();
  const { url } = useRouteMatch();
  const { parentCarouselId, parentCarouselType, bannerId } = useParams();
  const query = useQuery();

  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);
  const country = useCountry();

  const { carousel: parentCarousel } = useFetchCarousel({
    sourceCarouselId: parentCarouselId,
    source: PROVIDER.VRIO,
    type: parentCarouselType,
  });

  const currentCollection = useMemo(() => getMatchingCollection(
    parentCarousel?.collections,
    bannerId,
    query,
  ), [bannerId, parentCarousel?.collections, query]);

  const {
    title,
    description,
    backGroundImages,
  } = currentCollection || {};

  const [isScrolled, setIsScrolled] = useState(false);
  const [titleHeight, setTitleHeight] = useState(0);
  const titleRef = useRef();
  const scrollViewRef = useRef();
  const webAction = getWebAction(currentCollection);
  const isSportLander = SPORT_PAGE_SET.has(webAction?.text);

  const { imageUrl } = useImageCard({
    images: backGroundImages,
    imageSize: IMG_WIDTH,
    aspectRatio: IMG_DIMENSION,
  });

  const handleScroll = useCallback((scrollOffset) => {
    setIsScrolled(scrollOffset > 0);
  }, []);

  const handleInternalScroll = useCallback((scrollOffset) => {
    if (scrollOffset > 0) {
      setIsScrolled(true);
      scrollViewRef.current?.scrollToBottom();
    } else {
      setIsScrolled(false);
      scrollViewRef.current?.scrollToTop();
    }
  }, []);

  const cardsContainerStyle = useMemo(
    () => ({
      minHeight: `calc(${
        getRem(1080) - HEIGHT_DESCRIPTION_REM - MARGIN_BOTTOM_DESCRIPTION_REM
      }rem - ${titleHeight}px)`,
    }),
    [titleHeight],
  );

  // Reset state when changing page
  useEffect(() => {
    setIsScrolled(false);
    scrollViewRef.current?.scrollToTop();
  }, [url]);

  // Whenever the title height change update the state
  useEffect(() => {
    let timeout;
    if (titleRef?.current) {
      timeout = setTimeout(() => {
        setTitleHeight(titleRef.current.clientHeight);
      }, 300); // Animation takes 300ms
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isScrolled, title]);

  useEffect(() => {
    if (tagsMountRef.current && country && title && locationState) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.NETWORK_AND_COLLECTION_PAGE_DATA,
        subCategory: GTM_SUBCATEGORIES.PAGES.COLLECTION_PAGE,
        eventName: GTM_EVENT_NAMES.PAGE_VIEW,
        data: {
          collectionName: title,
          content: {
            playSource: formatPlaySource({
              parentLander: locationState?.parentLander,
              section: locationState?.carouselTitle,
            }) || locationState?.gtmPlaySource,
          },
        },
      });
      tagsMountRef.current = false;
    }
  }, [
    commonTaggingEvent,
    country,
    locationState,
    locationState?.carouselTitle,
    locationState?.gtmPlaySource,
    locationState?.parentLander,
    title,
  ]);

  return (
    <div
      className={`dtv-html5-c-catalog
        ${isSportLander ? 'dtv-html5-c-catalog--sport-lander' : ''
        } ${isScrolled ? 'dtv-html5-c-catalog--scrolled' : ''
        }`}
    >
      {!isSportLander && (
        <>
          <img
            className="dtv-html5-c-catalog__image"
            src={imageUrl}
            alt={title}
          />
          <div className="dtv-html5-c-catalog__title-container" ref={titleRef}>
            <h1 className="dtv-html5-c-catalog__title">{title}</h1>
          </div>
        </>
      )}
      <ScrollView
        className="dtv-html5-c-catalog__scroll-container"
        onScroll={handleScroll}
        ref={scrollViewRef}
      >
        {!isSportLander && (
          <div className="dtv-html5-c-catalog__description">
            <p>{description}</p>
          </div>
        )}

        {isSportLander ? (
          <>
            <div className="dtv-html5-c-catalog__header-container">
              <div className="dtv-html5-c-catalog__title-container" ref={titleRef}>
                <h1 className="dtv-html5-c-catalog__title">{title}</h1>
              </div>
              <img
                className="dtv-html5-c-catalog__image"
                src={imageUrl}
                alt={title}
              />
            </div>
            <CarouselList
              key={url}
              onScroll={handleInternalScroll}
              pageId={webAction?.target}
              sportIds={currentCollection?.sportIds}
              itemsContainerClassName="dtv-html5-c-catalog__carousel-list-items"
              className="dtv-html5-c-catalog__carousel-list"
              stealFocusOnMount
            />
          </>
        ) : (
          <CatalogVodLander
            key={url}
            className="dtv-html5-c-catalog__cards-container"
            cardsPerRow={CARDS_PER_ROW}
            style={cardsContainerStyle}
            onScrolledChange={setIsScrolled}
            focusFirstCard
            sourceCarouselId={bannerId}
            source={webAction?.text}
            catalogTitle={title}
          />
        )}
      </ScrollView>
    </div>
  );
}

export default memo(Catalog);
